import stores from "cd-eft/models/Stores";
import { DynamicCrossSectionTableStore } from "cd-eft/models/Stores/DynamicProfile/CrossSection/DynamicCrossSectionTableStore";
import { action, observable } from "mobx";
import { GroundPoint } from '../BreaklineTools/model/GroundPoint';
import { appConfig } from 'cd-eft/config/AppConfig';
import { EngineeringMapService } from 'cd-eft/framework/mapServices/EngineeringMapServices';
import sleep from 'cd-eft/utils/sleep';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import * as SETAPI from 'cd-eft/services/eftSetApi';
import { getDimensionsForImage } from "cd-eft/utils/imageUtils";


const surveyEngStore = stores.AppStore.surveyEngineeringStore;
const crossSectionStore = stores.AppStore.crossSectionDesignStore;
const designStore = stores.AppStore.designStore;
const waterwayDesignStore = stores.AppStore.waterwayDesignStore;
const terraceDesignStore = stores.AppStore.terraceDesignStore;

interface IProfilePoint {
    id: number;
    x: number;
    y: number;
    pointDistance: number;
    originalElevation: number;
    designElevation: number;
    cutFillHeight: number;
}

export class CrossSectionVM {
    private tableStore: DynamicCrossSectionTableStore;
    @observable props;
    currentObject;
    @observable isSuccessful: boolean = false;
    @observable error = false;
    @observable errorMsg = '';
    @observable thresholdSize: boolean = false;
    @observable replaceTheOldestImage: boolean = false;
    @observable saveReportClicked: boolean = false;
    @observable popupWarningMessage = '';
    @observable private _continueExecution: boolean = false;
    private watchHandle;

    constructor(props: any) {
        this.props = props;

        this.currentObject = crossSectionStore.crossSectionObject;
        this.currentObject.editingStatus = !crossSectionStore.currentElement.id ? 'add' : 'update';
        this.currentObject.surfaceDefId = designStore.currentSurface.surfaceDefId;
        this.currentObject.surfaceDefTitle = designStore.currentSurface.surfaceDefTitle;
        this.currentObject.sessionId = designStore.projectsStore.currentSessionId;

        this.tableStore = props.profileTableStore;
        this.tableStore.setStore = surveyEngStore;
        this.tableStore.crossSectionStore = crossSectionStore;
        this.tableStore.CurrentObject = crossSectionStore.crossSectionObject;
        this.isSuccessful = false;


    }

    @action.bound sketchCrossSection(e: any) {
        e.preventDefault();
        this.isSuccessful = false;
        this.error = false;
        this.tableStore.sketchPolyline();
    }

    @action.bound onMount() {
        const dragElement = document.getElementById('scs-header');
        this.watchHandle = dragElement?.addEventListener('mousedown', this.handleMouseDown);
    }

    @action.bound onUnmount() {
        this.watchHandle?.remove();
    }

    //insert Point / vertex
    @action.bound insertPoint(e: any) {
        e.preventDefault();
        this.tableStore.insertStation();
    }

    private handleMouseDown(e) {
        const crossSectionElement = document.getElementById('single-crosssection-container');
        if (crossSectionElement) {
            document.body.style.userSelect = 'none';
            const offsetX = e.clientX - crossSectionElement.offsetLeft;
            const offsetY = e.clientY - crossSectionElement.offsetTop;

            const handleMouseMove = (e) => {
                const left = e.clientX - offsetX;
                const top = e.clientY - offsetY;
                const designDiv = document.getElementById('design-container');
                const maxX = designDiv!.clientWidth - crossSectionElement!.offsetWidth;
                const maxY = designDiv!.clientHeight - crossSectionElement!.offsetHeight;

                crossSectionElement!.style.left = `${Math.min(maxX, Math.max(0, left))}px`;
                crossSectionElement!.style.top = `${Math.min(maxY, Math.max(0, top))}px`;
            }

            const handleMouseUp = () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
                document.body.style.userSelect = 'auto';
            }

            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }
    }

    handleRowMouseEnter(data) {
        const point = new GroundPoint();
        point.id = data.stationName;
        point.name = data.stationName;
        point.x = data.x;
        point.y = data.y;
        point.z = data.z;
        point.wkid = appConfig.tempHardCodes.wkid;
        EngineeringMapService.instance?.trackPoint(point);
    }

    handleRowMouseLeave() {
        EngineeringMapService.instance?.unTrackLast();
    }

    @action.bound onClickCancelCrossSec = () => {
        designStore.hideCrossSectionToolbar = true;
        designStore.hideSingleCrossSection = true;
        designStore.crossSectionDrawingComplete = false;
    };

    @action.bound async onClickSaveCrossSec(profileView: any, mapView: any) {
        designStore.setLoader("Saving the Report...");
        this.saveReportClicked = true;
        await sleep(1000); // let it redraw
        await this.takeProfileViewScreenshot(profileView, mapView);
        designStore.setLoader("");
    }

    @action.bound
    handleConfirmAction(profileView: any, mapView: any) {
        this.saveReportClicked = false;
        this.thresholdSize = false;
        this.replaceTheOldestImage = true;
        this._continueExecution = true;
        this.takeProfileViewScreenshot(profileView, mapView);
    }

    @action.bound
    handleCancelAction() {
        this.saveReportClicked = false;
        this.thresholdSize = false;
        this.replaceTheOldestImage = false;
        this._continueExecution = false;
        this.onClickCancelCrossSec();

    }
    private async takeProfileViewScreenshot(profileView: any, mapView: any): Promise<void> {
        if (!this._continueExecution) {
            const response = await SETAPI.StructureProfileController.isImageCountAboveThresholdUsingGET(designStore.singleCrossSectionDesignElementId, 10);
            this.thresholdSize = response.data;
            if (this.thresholdSize) {
                this.popupWarningMessage = await MessageCenter.addErrorLabelFromCode('Profile_01');
            }
            if (this.thresholdSize && !this.replaceTheOldestImage) {
                this.saveReportClicked = true;
                return;
            }
        }

        try {
            // Get the extent of the mapView
            const extent = profileView.extent;

            const dimensions = getDimensionsForImage(extent, profileView);

            // Take a screenshot of the profileView with the specified parameters
            const screenshot = await profileView.takeScreenshot({
                width: dimensions.imageWidth,
                height: dimensions.imageHeight,
                area: dimensions.area,
                format: 'png',
                ignorePadding: true
            });
            const profileUrl = screenshot.dataUrl;

            // // Take a screenshot of the mapView
            const mapScreenshot = await this.takeMapViewScreenshot(mapView);
            const mapUrl = mapScreenshot.dataUrl;
            // Get the cross-section stations
            const stations = this.currentObject.crossSectionPoints;

            // Prepare the payload for generating/updating the structure profile
            const twoDimensionalGeometry = {
                profileOrCrossSectionXY: stations,
            };

            const getDesignElementTypeId = () => {
                if (terraceDesignStore && terraceDesignStore.terraceType) {
                    return terraceDesignStore.terraceType;
                }

                if (waterwayDesignStore.currentWaterwayDesignElementTypeId) {
                    return waterwayDesignStore.currentWaterwayDesignElementTypeId;
                }

                return designStore.designElementTypeId;
            };

            //payload for crosssection
            const payloadCS = {
                designElementId: designStore.singleCrossSectionDesignElementId,
                designElementTypeId: getDesignElementTypeId(),
                profileImageBase64String: profileUrl,
                profileOrCrossSection: "CrossSection",
                stationId: undefined,
                structureMapImageBase64String: undefined,
                structureProfilesId: 0,
                twoDimensionalGeometryJson: JSON.stringify(twoDimensionalGeometry)
            };


            // Call the API
            this.isSuccessful = false;
            const response1 = await SETAPI.StructureProfileController.generateUpdateStructureProfileUsingPOST(payloadCS)

            if (response1.data) {
                this.isSuccessful = true;
            }

            designStore.crossSectionDrawingComplete = false;
            this.replaceTheOldestImage = false;
        } catch (error) {
            // Handle any errors that occur during the process
            this.error = true;
            this.errorMsg = (error as Error).message;
            //MessageCenter.addError((error as Error).message);
        } finally {
            this._continueExecution = false;
        }
    }

    private async takeMapViewScreenshot(mapView: any): Promise<any> {
        try {
            // Get the extent of the mapView
            const extent = mapView.extent;

            const dimensions = getDimensionsForImage(extent, mapView);

            // Take a screenshot of the mapView with the specified parameters
            const screenshot = await mapView.takeScreenshot({
                width: dimensions.imageWidth,
                height: dimensions.imageHeight,
                area: dimensions.area,
                format: 'png',
                ignorePadding: true
            });

            return screenshot;
        } catch (error) {
            // Handle any errors that occur during the process
            this.error = true;
            this.errorMsg = (error as Error).message;
            // throw is used to handle exception. so that it propagates to the parent catch
            throw error;
        }

    }
}